import React, { useEffect, useState } from 'react';
import { supabase } from '../services/supabase';
import { SignalCard, SignalData } from './SignalCard';

interface Props {
    scannerType: '313' | 'elite_53' | 'gemini-2.5-pro';
    isPremium: boolean;
    limit?: number;
}

export const SignalsPreview: React.FC<Props> = ({ scannerType, isPremium, limit = 6 }) => {
    const [signals, setSignals] = useState<SignalData[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!isPremium) { setLoading(false); return; }

        const fetchSignals = async () => {
            // BUG-15 FIX: fetch BOTH active AND pending — pending limit orders were invisible
            const { data: activeData, error: activeErr } = await supabase
                .from('signals')
                .select('*')
                .eq('scanner_type', scannerType)
                .in('result', ['active', 'pending'])
                .order('created_at', { ascending: false })
                .limit(limit);

            if (activeErr) console.error('Error fetching active signals:', activeErr);
            else if (activeData) setSignals(activeData as SignalData[]);

            if (activeErr) console.error('Error fetching active signals:', activeErr);
            else if (activeData) setSignals(activeData as SignalData[]);

            setLoading(false);
        };

        fetchSignals();

        // Realtime subscription
        const channel = supabase
            .channel(`public:signals:${scannerType}`)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'signals',
                    filter: `scanner_type=eq.${scannerType}`
                },
                (payload) => {
                    console.log('Realtime signal update:', payload);
                    if (payload.eventType === 'INSERT') {
                        const newSig = payload.new as SignalData;
                        // Only add to active list if it's active or pending
                        if (['active', 'pending'].includes(newSig.result)) {
                            setSignals((prev) => [newSig, ...prev].slice(0, limit));
                        }
                    } else if (payload.eventType === 'UPDATE') {
                        const updated = payload.new as SignalData;
                        // BUG-16 FIX: if signal closed — remove from active list
                        if (['win', 'loss', 'invalid'].includes(updated.result)) {
                            setSignals((prev) => prev.filter(sig => sig.id !== updated.id));
                        } else {
                            // Still active/pending — update in list
                            setSignals((prev) => prev.map(sig => sig.id === updated.id ? updated : sig));
                        }
                    } else if (payload.eventType === 'DELETE') {
                        setSignals((prev) => prev.filter(sig => sig.id !== payload.old.id));
                    }
                }
            )
            .subscribe();

        return () => { supabase.removeChannel(channel); };
    }, [scannerType, isPremium, limit]);

    if (!isPremium) {
        return (
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 text-center relative overflow-hidden">
                <div className="absolute inset-0 bg-finance-dark/50 backdrop-blur-[2px]"></div>
                <div className="relative z-10">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <h3 className="text-xl font-bold text-white mb-2">Institutional AI Signals Locked</h3>
                    <p className="text-gray-400 mb-6 max-w-md mx-auto">Upgrade to access real-time AI validated trading signals from our institutional quant models.</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
                {[1, 2, 3].map(i => (
                    <div key={i} className="bg-gray-800/50 rounded-2xl h-64 border border-gray-700/50"></div>
                ))}
            </div>
        );
    }

    const getClosedOutcome = (sig: SignalData) => {
        if (sig.result === 'win' && (sig as any).status === 'be_closed') return { label: 'BE Closed (TP1 ✓)', color: 'text-amber-400 bg-amber-400/10 border-amber-400/20' };
        if (sig.result === 'win' && (sig as any).tp2) return { label: 'TP2 ✓', color: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20' };
        if (sig.result === 'win') return { label: 'TP1 ✓', color: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20' };
        if (sig.result === 'loss') return { label: 'SL Hit', color: 'text-rose-400 bg-rose-400/10 border-rose-400/20' };
        return { label: 'Expired', color: 'text-gray-400 bg-gray-400/10 border-gray-400/20' };
    };

    const getRRDisplay = (sig: SignalData) => {
        const rr = (sig as any).rr_ratio;
        if (typeof rr !== 'number') return '—';
        return rr >= 0 ? `+${rr.toFixed(2)}R` : `${rr.toFixed(2)}R`;
    };

    return (
        <div className="space-y-10">
            {/* Agent Army Pipeline Status */}
            {scannerType === 'gemini-2.5-pro' && (
                <div className="flex flex-wrap items-center justify-between gap-4 p-4 rounded-2xl bg-[#0b0f19]/80 border border-finance-accent/20 backdrop-blur-xl relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-finance-accent"></div>
                    <div className="flex items-center gap-4 relative z-10">
                        <div className="flex -space-x-2">
                            {[1, 2, 3, 4, 5].map((i) => (
                                <div key={i} className={`w-8 h-8 rounded-full border-2 border-[#0b0f19] bg-gray-800 flex items-center justify-center text-[11px] font-black ${i === 5 ? 'bg-finance-accent text-finance-dark' : 'text-gray-400'}`}>
                                    A{i}
                                </div>
                            ))}
                        </div>
                        <div>
                            <h5 className="text-finance-accent text-xs font-black tracking-widest uppercase">ICT Agent Army Pipeline</h5>
                            <p className="text-[11px] text-gray-400 font-bold uppercase tracking-tighter">
                                {signals.filter(s => s.result === 'active').length} Active •&nbsp;
                                {signals.filter(s => s.result === 'pending').length} Pending Orders
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 relative z-10">
                        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-500/10 border border-green-500/20">
                            <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
                            <span className="text-[11px] text-green-400 font-black uppercase tracking-widest">Network Live</span>
                        </div>
                        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20">
                            <span className="text-[11px] text-blue-400 font-black uppercase tracking-widest">Neural Engine Active</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Active + Pending Signals Grid */}
            {signals.length === 0 ? (
                <div className="bg-[#0b0f19] border border-gray-800/60 rounded-2xl p-12 text-center text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-4 opacity-50 text-finance-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-lg font-medium">No Active Signals Currently</p>
                    <p className="text-sm mt-2">Our AI is monitoring the markets for the next high-probability setup.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mt-12">
                    {signals.map((sig) => (
                        <SignalCard key={sig.id} signal={sig} />
                    ))}
                </div>
            )}
        </div>
    );
};
